import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/supabase-auth';
import { canAccessRoute, getRedirectPath } from '@/lib/rbac';
import { UserRole } from '@/types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  redirectTo?: string;
  fallback?: React.ReactNode;
}

/**
 * Componente para proteger rotas individuais
 * Usado diretamente no roteador para verificar acesso antes de renderizar a página
 */
export function ProtectedRoute({ 
  children, 
  allowedRoles, 
  redirectTo,
  fallback 
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Aguardar carregamento da autenticação
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Se não está autenticado, redirecionar para login
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar papel específico se especificado
  if (allowedRoles && !allowedRoles.includes(user.papel)) {
    const defaultRedirect = getRedirectPath(user.papel);
    return <Navigate to={redirectTo || defaultRedirect} replace />;
  }

  // Verificar se pode acessar a rota atual
  if (!canAccessRoute(user, location.pathname)) {
    const defaultRedirect = getRedirectPath(user.papel);
    return <Navigate to={redirectTo || defaultRedirect} replace />;
  }

  // Se chegou até aqui, tem acesso
  return <>{children}</>;
}

/**
 * Mapeamento de rotas para papéis permitidos
 * Usado para verificação de acesso baseada na URL
 */
export const ROUTE_ACCESS_MAP: Record<string, UserRole[]> = {
  // Painel - PRODUCAO e AGIS
  '/painel': ['PRODUCAO', 'AGIS'],
  
  // Eventos - apenas PRODUCAO (gestão completa)
  '/eventos': ['PRODUCAO'],
  
  // Participantes - apenas PRODUCAO
  '/participantes': ['PRODUCAO'],
  
  // Agenda - AGIS e PRODUCAO (monitoramento e gestão)
  '/agenda': ['AGIS', 'PRODUCAO'],
  '/agenda-switcher': ['AGIS', 'PRODUCAO'],
  
  // Minha Agenda - todos exceto casos específicos
  '/minha-agenda': ['FABRICANTE', 'REVENDA', 'AGIS', 'PRODUCAO'],
  
  // Mapa - todos os papéis
  '/mapa': ['AGIS', 'PRODUCAO', 'FABRICANTE', 'REVENDA'],
  
  // Usuários - apenas PRODUCAO
  '/usuarios': ['PRODUCAO'],
  
  // Branding - apenas PRODUCAO
  '/branding': ['PRODUCAO'],
  
  // Configurações - apenas PRODUCAO
  '/configuracoes': ['PRODUCAO'],
  '/configuracao-associacoes': ['PRODUCAO'],
  
  // Ajustes Horários - apenas PRODUCAO
  '/ajustes-horarios': ['PRODUCAO'],
};

/**
 * Verifica se um usuário pode acessar uma rota específica
 */
export function canUserAccessRoute(userRole: UserRole, pathname: string): boolean {
  // Verificar rotas exatas primeiro
  const exactMatch = ROUTE_ACCESS_MAP[pathname];
  if (exactMatch) {
    return exactMatch.includes(userRole);
  }

  // Verificar rotas que começam com o caminho (para sub-rotas)
  for (const [route, allowedRoles] of Object.entries(ROUTE_ACCESS_MAP)) {
    if (pathname.startsWith(route) && route !== '/') {
      return allowedRoles.includes(userRole);
    }
  }

  // Para rotas não mapeadas, permitir acesso por padrão
  // (podem ser rotas públicas ou que não requerem controle específico)
  return true;
}